import { observable, action, makeObservable, runInAction } from "mobx";
import { RootStore } from "./RootStore";
import { FILE_ENTITY, FILE_TYPE } from "src/enums";
import { IFile } from "src/types";
import { ErpType, Role } from "src/network/User";
import { MessageType } from "src/components/notifications/Notifier";
import Axios from "src/network/Axios";
import { NetworkConfig } from "src/network/NetworkConfig";
import i18next from "i18next";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { DELETE_NEWSFILES_OF_NEWS, GET_NEWS_SELECTION_FILES_MANAGER } from "src/api/news";
import { DeleteNewsFilesOfNews, DeleteNewsFilesOfNewsVariables } from "src/api/generated/DeleteNewsFilesOfNews";
import {
    GetNewsSelectionFilesManager,
    GetNewsSelectionFilesManagerVariables
} from "src/api/generated/GetNewsSelectionFilesManager";
import { ISelectableRealestate } from "src/stores/RealestateStore";
import { getNewsFileIdsToDetach, getNewsSelectionFilesVariables, getRemovedRealestates } from "src/utils/News";

export class NewsStore {
    rootStore: RootStore;

    isUploadingExternalFile: boolean = false;
    isSavingNews: boolean = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            isUploadingExternalFile: observable,
            isSavingNews: observable,
            setIsUploadingExternalFile: action,
            saveNews: action
        });
    }

    setIsUploadingExternalFile = (value: boolean) => {
        this.isUploadingExternalFile = value;
    };

    private fileToBase64DataOnly = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error);
            reader.onload = () => {
                const result = String(reader.result ?? "");
                const commaIndex = result.indexOf(",");
                resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
            };
            reader.readAsDataURL(file);
        });
    };

    private buildIFileFromBrowserFile = async (file: File, type: number): Promise<IFile> => {
        const base64String = await this.fileToBase64DataOnly(file);
        const extension = (file.name.split(".").pop() ?? "pdf").toLowerCase();

        const filename = file.name.endsWith(`.${extension}`) ? file.name.replace(`.${extension}`, "") : file.name;

        return {
            mimetype: file.type || "application/pdf",
            extension,
            name: filename,
            fileDate: new Date().toISOString(),
            base64String,
            type
        } as IFile;
    };

    saveNews = async (requestBody: object, role: string): Promise<boolean> => {
        runInAction(() => {
            this.isSavingNews = true;
        });
        try {
            await Axios.getAuthInstance().put(NetworkConfig.upsertNewsUrl, requestBody, {
                headers: { "x-hasura-role": role }
            });
            return true;
        } catch (e) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.realestate.news_edit.save_error") + (e instanceof Error ? e.message : ""),
                MessageType.ERROR
            );
            console.error(e);
            return false;
        } finally {
            runInAction(() => {
                this.isSavingNews = false;
            });
        }
    };

    // Residents see every file attached to a news they can see, whichever realestate it comes from. Files of a
    // realestate that was removed from the news are therefore detached once the news is saved.
    detachFilesOfRemovedRealestates = async (
        newsid: string,
        savedRealestates: ISelectableRealestate[],
        selectedRealestates: ISelectableRealestate[],
        attachedFileIds: string[]
    ): Promise<void> => {
        const removedRealestates = getRemovedRealestates(savedRealestates, selectedRealestates);
        if (removedRealestates.length === 0 || attachedFileIds.length === 0) {
            return;
        }

        try {
            const [fileIdsOfRemovedRealestates, fileIdsOfRemainingRealestates] = await Promise.all([
                this.queryNewsSelectionFileIds(removedRealestates),
                this.queryNewsSelectionFileIds(selectedRealestates)
            ]);
            const fileIdsToDetach = getNewsFileIdsToDetach(
                attachedFileIds,
                fileIdsOfRemovedRealestates,
                fileIdsOfRemainingRealestates
            );
            if (fileIdsToDetach.length === 0) {
                return;
            }

            await apolloClientInstance.mutate<DeleteNewsFilesOfNews, DeleteNewsFilesOfNewsVariables>({
                mutation: DELETE_NEWSFILES_OF_NEWS,
                variables: { newsid, fileids: fileIdsToDetach }
            });
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.realestate.news_edit.detach_newsfiles_success"),
                MessageType.INFO
            );
        } catch (e) {
            console.error(e);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.realestate.news_edit.detach_newsfiles_error"),
                MessageType.ERROR
            );
        }
    };

    private queryNewsSelectionFileIds = async (realestates: ISelectableRealestate[]): Promise<string[]> => {
        const isIT2 = this.rootStore.authStore.user?.erpType === ErpType.IT2;
        const { data } = await apolloClientInstance.query<
            GetNewsSelectionFilesManager,
            GetNewsSelectionFilesManagerVariables
        >({
            query: GET_NEWS_SELECTION_FILES_MANAGER,
            variables: getNewsSelectionFilesVariables(realestates, isIT2),
            fetchPolicy: "no-cache"
        });

        return [...data.accountlistfiles, ...data.realestatefiles].map((file) => file.fileid);
    };

    uploadExternalNewsFile = async (file: File, newsid: string): Promise<string | undefined> => {
        runInAction(() => {
            this.isUploadingExternalFile = true;
        });

        try {
            const metadataFile = await this.buildIFileFromBrowserFile(file, FILE_TYPE.NEWS_EXTERNAL_ATTACHMENT);
            metadataFile.fileBlob = file;

            const newFileId = await this.rootStore.fileStore.uploadFilesBulkWithRoles(
                metadataFile,
                newsid,
                FILE_ENTITY.NEWS_ITEM,
                [Role.MANAGER, Role.COOWNER]
            );

            return newFileId;
        } catch (e) {
            console.error(e);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.realestate.news_edit.error_upladfile") + (e instanceof Error ? e.message : ""),
                MessageType.ERROR
            );
            return undefined;
        } finally {
            runInAction(() => {
                this.isUploadingExternalFile = false;
            });
        }
    };
}
